import type { CSSProperties, ReactNode } from "react";
import { tokens } from "../theme/tokens";
import { tableSpacing } from "./tableSpacing";

// Botón de acción de fila de tabla: variante fija de `.chip`.
//
// `CursorTable` solo estilizaba el contenedor de `rowActions` (gap +
// alineación), así que cada pantalla armaba su propio `className="chip"` con
// el padding a mano y divergieron en cuatro tamaños distintos para el mismo
// botón: `.chip` base sin override (9px 15px / 12px) en Catálogos y Perfiles,
// "5px 6px" con fuente 10px en Usuarios, "7px 11px" en Círculo de miembros y
// Vehículos, y en Motor de Puntos el padding lo pasaba quien llamaba.
//
// El padding único vive en `tableSpacing.actionPadding`. La clase `chip` se
// conserva —`base.css` no se toca— y solo se sobrescribe el padding.

export type TableRowActionTone = "default" | "danger" | "accent";

// Los tonos que ya existían sueltos en las pantallas se preservan: `danger`
// para acciones consecuentes (tokens.danger.base/line) y `accent` para el
// champagne de "Registrar acción" (tokens.gold.light/line).
const toneStyle: Record<TableRowActionTone, CSSProperties> = {
  default: {},
  danger: { color: tokens.danger.base, borderColor: tokens.danger.line },
  accent: { color: tokens.gold.light, borderColor: tokens.gold.line },
};

export interface TableRowActionButtonProps {
  children: ReactNode;
  onClick: () => void;
  tone?: TableRowActionTone;
  disabled?: boolean;
  title?: string;
  "aria-label"?: string;
  /**
   * `"icon"` cambia SOLO el padding, al valor que deja la caja del mismo alto
   * que la del botón con texto (ver `tableSpacing.iconActionPadding`). El
   * ícono y su tooltip los pone `RowIconAction` (`@portal/common`), que es
   * quien conoce el `Tooltip`: este módulo vive en `@core` y no puede
   * importar de `@portal` (ADR-011).
   */
  variant?: "text" | "icon";
  /**
   * Lo inyecta `Tooltip` al clonar su único hijo. Sin declararlo y
   * reenviarlo, `cloneElement` se lo pasa a ESTE componente como prop y
   * nunca llega al `<button>`: el tooltip quedaría sin asociación accesible.
   */
  "aria-describedby"?: string;
}

export function TableRowActionButton({
  children,
  onClick,
  tone = "default",
  disabled,
  title,
  "aria-label": ariaLabel,
  variant = "text",
  "aria-describedby": ariaDescribedBy,
}: TableRowActionButtonProps) {
  return (
    <button
      type="button"
      className="chip"
      style={{
        padding:
          variant === "icon"
            ? tableSpacing.iconActionPadding
            : tableSpacing.actionPadding,
        ...toneStyle[tone],
        // `.chip` (base.css) no tiene regla `:disabled`: un botón de acción
        // deshabilitado se veía y se sentía idéntico a uno activo. Las
        // pantallas que lo notaban lo compensaban a mano —"Subir"/"Bajar" del
        // carrusel de Inicio pasaban `opacity: 0.4` en su `style`— y las que
        // no ("Marcar contactado" mientras
        // la mutación está en vuelo) quedaban sin ninguna señal. Al mover el
        // estilo a este primitivo deja de depender de que cada pantalla se
        // acuerde. El 0.4 es el valor que ya usaba Inicio, no uno nuevo.
        ...(disabled ? { opacity: 0.4, cursor: "default" } : {}),
      }}
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-label={ariaLabel}
      aria-describedby={ariaDescribedBy}
    >
      {children}
    </button>
  );
}
